"use client";
import { useTranslations } from "@zapier/i18n";

import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  BetaDataTableCell as Cell,
  VisuallyHidden,
  useBetaDataTableContext as useTableContext,
} from "@zapier/design-system";
import {
  FolderContext,
  FolderTreeItem,
  useFolderActionPermissions,
} from "@zapier/client";
import { OwnerCell, Row, TitleCell, FolderModal } from "@zapier/shared";
import { AssetTypes, getFolderUrl, isPersonalFolder } from "@zapier/utils";
import styles from "./FolderRow.module.css";
import { useFolderActionItems } from "../../../hooks/useFolderActionItems";

interface Props {
  folder: FolderTreeItem;
  folderLevel: number;
  /**
   * Absolute depth of the current folder (used to convert the row-relative `folderLevel`).
   */
  currentFolderDepth?: number;
  folderZapTableRow?: boolean;
  isFolderExpanded?: boolean;
  isFoldersExpandedByDefault?: boolean;
  onExpand?: (isFolderExpanded: boolean) => void;
  searchQuery?: string | null;
  showLastRun?: boolean;
  showLocationColumn?: boolean;
  isTrashView?: boolean;
  includeSelectionColumn?: boolean;
  hasSelectedAncestor?: boolean;
  setCurrentModal: (modal: FolderModal | undefined) => void;
  setCurrentFolder: (folder: FolderTreeItem | undefined) => void;
  isChildRow?: boolean;
}

function getDescendantFolderRowIds(folder: FolderTreeItem): string[] {
  const descendants: string[] = [];
  const queue = [...(folder.children || [])];

  while (queue.length > 0) {
    const child = queue.shift() as FolderTreeItem;
    descendants.push(`folder:${child.id}`);
    if (child.children?.length) {
      queue.push(...child.children);
    }
  }

  return descendants;
}

const FolderRowBranch = ({
  folder,
  folderLevel,
  currentFolderDepth,
  folderZapTableRow,
  isFolderExpanded,
  onExpand,
  searchQuery,
  showLastRun,
  showLocationColumn = true,
  isTrashView = false,
  setCurrentModal,
  setCurrentFolder,
  isChildRow,
  includeSelectionColumn,
  hasSelectedAncestor,
}: Props) => {
  const t = useTranslations("folder-table-row");

  // In the folder page table, `folderLevel` is relative (starts at 0 for direct children).
  // Convert row-relative `folderLevel` into an absolute depth for permission + depth-limit checks.
  const absoluteFolderLevel = (currentFolderDepth ?? 0) + 1 + folderLevel;
  const { enablePermissionsCheck } = useFolderActionPermissions({
    folderId: folder.id,
    folderDepth: absoluteFolderLevel,
  });

  const folderActionItems = useFolderActionItems({
    folder,
    folderLevel: absoluteFolderLevel,
    setCurrentFolder,
    setCurrentModal,
  });

  return (
    <Row
      allowsSelection={
        !!includeSelectionColumn &&
        !hasSelectedAncestor &&
        !isPersonalFolder(folder.id)
      }
      hiddenSelection={
        !!includeSelectionColumn &&
        (hasSelectedAncestor || isPersonalFolder(folder.id))
      }
      id={`folder:${folder.id}`}
      assetName={folder.name}
      kebabMenuLabel={t("menus.folder-options")}
      kebabMenuAriaLabel={t("menus.folder-options-for", { name: folder.name })}
      menuItems={folderActionItems}
      onOpenMenu={enablePermissionsCheck}
      assetType={AssetTypes.Folder}
      className={`${isChildRow ? styles.childRow : ""} ${folderZapTableRow ? styles.folderZapTableRow : ""}`}
    >
      <TitleCell
        color="GrayWarm6"
        icon={folder.isShared ? "team" : "navFolder"}
        title={folder.name}
        searchQuery={searchQuery}
        url={getFolderUrl(folder.id)}
        id={folder.id}
        accordionLevel={folderLevel}
        hasAccordion={!!folder.children?.length}
        allowAccordion={true}
        isAccordionExpanded={isFolderExpanded}
        onExpandAccordion={onExpand}
        hiddenSelection={includeSelectionColumn}
        assetType={AssetTypes.Folder}
        badge={
          folder.assetCount === undefined ? undefined : (
            <Badge variant="neutral">
              {folder.assetCount.toLocaleString()}
            </Badge>
          )
        }
      />

      {folderZapTableRow && (
        <>
          {!isTrashView && (
            <>
              <Cell>
                <VisuallyHidden>{t("columns.status")}</VisuallyHidden>
              </Cell>
              <Cell>
                <VisuallyHidden>{t("columns.apps")}</VisuallyHidden>
              </Cell>
            </>
          )}
          {showLocationColumn && (
            <Cell>
              <VisuallyHidden>{t("columns.location")}</VisuallyHidden>
            </Cell>
          )}
          <Cell>
            <VisuallyHidden>{t("columns.last-modified")}</VisuallyHidden>
          </Cell>
          {showLastRun && (
            <Cell>
              <VisuallyHidden>{t("columns.last-run")}</VisuallyHidden>
            </Cell>
          )}
        </>
      )}
      <OwnerCell
        name={folder.owner?.fullName}
        photoUrl={folder.owner?.photoUrl}
      />
    </Row>
  );
};

const FolderRow = ({
  folder,
  folderLevel = 0,
  currentFolderDepth,
  folderZapTableRow,
  searchQuery,
  showLastRun,
  showLocationColumn = true,
  isTrashView = false,
  isFoldersExpandedByDefault,
  includeSelectionColumn,
  hasSelectedAncestor = false,
  setCurrentFolder,
  setCurrentModal,
  isChildRow,
}: Props) => {
  const [isFolderExpanded, setIsFolderExpanded] = useState(false);
  const { selectedRows = [], setSelectedRows } = useTableContext();
  const rowId = `folder:${folder.id}`;
  const isCurrentFolderSelected = selectedRows.includes(rowId);
  const descendantFolderRowIds = useMemo(
    () => getDescendantFolderRowIds(folder),
    [folder],
  );

  useEffect(() => {
    // Expand folders by default if search query is present
    if (isFoldersExpandedByDefault) {
      setIsFolderExpanded(true);
    } else {
      // Close folders if search query is removed
      setIsFolderExpanded(false);
    }
  }, [folder, isFoldersExpandedByDefault]);

  useEffect(() => {
    if (!isCurrentFolderSelected || descendantFolderRowIds.length === 0) {
      return;
    }

    const descendantIdSet = new Set(descendantFolderRowIds);
    const nextSelectedRows = selectedRows.filter(
      (id) => !descendantIdSet.has(id),
    );

    if (nextSelectedRows.length !== selectedRows.length) {
      setSelectedRows(nextSelectedRows);
    }
  }, [
    descendantFolderRowIds,
    isCurrentFolderSelected,
    selectedRows,
    setSelectedRows,
  ]);

  useEffect(() => {
    if (!hasSelectedAncestor || !isCurrentFolderSelected) {
      return;
    }

    const nextSelectedRows = selectedRows.filter((id) => id !== rowId);
    if (nextSelectedRows.length !== selectedRows.length) {
      setSelectedRows(nextSelectedRows);
    }
  }, [
    hasSelectedAncestor,
    isCurrentFolderSelected,
    rowId,
    selectedRows,
    setSelectedRows,
  ]);

  return (
    <FolderContext.Provider value={folder}>
      <FolderRowBranch
        folder={folder}
        folderLevel={folderLevel}
        currentFolderDepth={currentFolderDepth}
        folderZapTableRow={folderZapTableRow}
        isFolderExpanded={isFolderExpanded}
        onExpand={setIsFolderExpanded}
        searchQuery={searchQuery}
        showLastRun={showLastRun}
        showLocationColumn={showLocationColumn}
        isTrashView={isTrashView}
        includeSelectionColumn={includeSelectionColumn}
        hasSelectedAncestor={hasSelectedAncestor}
        setCurrentFolder={setCurrentFolder}
        setCurrentModal={setCurrentModal}
        isChildRow={isChildRow}
      />
      {/* Recursively render children in the same row */}
      {isFolderExpanded &&
        folder?.children?.map((child) => (
          <FolderRow
            folder={child as FolderTreeItem}
            folderLevel={folderLevel + 1}
            currentFolderDepth={currentFolderDepth}
            folderZapTableRow={folderZapTableRow}
            showLastRun={showLastRun}
            showLocationColumn={showLocationColumn}
            isTrashView={isTrashView}
            isFoldersExpandedByDefault={isFoldersExpandedByDefault}
            key={child.id}
            searchQuery={searchQuery}
            includeSelectionColumn={includeSelectionColumn}
            hasSelectedAncestor={hasSelectedAncestor || isCurrentFolderSelected}
            setCurrentFolder={setCurrentFolder}
            setCurrentModal={setCurrentModal}
            isChildRow={true}
          />
        ))}
    </FolderContext.Provider>
  );
};

export default FolderRow;
